// A simulated log-magnitude Fourier spectrum of a generated image, for the
// hero figure. Real camera spectra fall off smoothly from the center (plus
// the bright cross every finite image has from its own edges); generators
// that upsample add a lattice of periodic peaks on top. Nothing here is a
// real measurement - it's drawn to show what the signal looks like.

// Peak positions in normalized frequency (-1..1 on each axis, 0 = DC).
// The strong ones sit on the half-Nyquist lattice that 2x upsampling
// leaves; the fainter quarter-lattice ones come from a second stage.
export const SPECTRUM_PEAKS = [
    { u: 0.5, v: 0, strength: 1 },
    { u: -0.5, v: 0, strength: 1 },
    { u: 0, v: 0.5, strength: 1 },
    { u: 0, v: -0.5, strength: 1 },
    { u: 0.5, v: 0.5, strength: 0.8 },
    { u: -0.5, v: 0.5, strength: 0.8 },
    { u: 0.5, v: -0.5, strength: 0.8 },
    { u: -0.5, v: -0.5, strength: 0.8 },
    { u: 0.25, v: 0.25, strength: 0.5 },
    { u: -0.25, v: 0.25, strength: 0.5 },
    { u: 0.25, v: -0.25, strength: 0.5 },
    { u: -0.25, v: -0.25, strength: 0.5 },
]

// The brand ramp, abyss -> volt -> indigo -> ultraviolet -> near-white, so
// the brightest energy reads in the Sigma's own colors.
const RAMP = [
    [0, [3, 23, 38]],
    [0.22, [6, 42, 71]],
    [0.45, [27, 154, 255]],
    [0.66, [81, 65, 255]],
    [0.84, [157, 5, 255]],
    [1, [246, 236, 255]],
]

function rampColor(value) {
    for (let index = 1; index < RAMP.length; index++) {
        const [stop, color] = RAMP[index]
        if (value <= stop) {
            const [previousStop, previousColor] = RAMP[index - 1]
            const t = (value - previousStop) / (stop - previousStop)
            return previousColor.map((channel, c) => Math.round(channel + (color[c] - channel) * t))
        }
    }
    return RAMP[RAMP.length - 1][1]
}

// Seeded so the figure is identical on every load
function seededRandom(seed) {
    let state = seed
    return () => {
        state = (state * 1664525 + 1013904223) % 4294967296
        return state / 4294967296
    }
}

function gaussian(distanceSquared, width) {
    return Math.exp(-distanceSquared / (2 * width * width))
}

export function buildSpectrumTexture(size = 256) {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const context = canvas.getContext('2d')
    const image = context.createImageData(size, size)
    const random = seededRandom(7)
    const half = size / 2

    for (let y = 0; y < size; y++) {
        for (let x = 0; x < size; x++) {
            const u = (x - half) / half
            const v = (y - half) / half
            const radius = Math.sqrt(u * u + v * v)

            // Smooth 1/f-style falloff with speckle, like any natural image
            let value = 0.78 * Math.exp(-radius * 2.6) + 0.16 * Math.max(0, 1 - radius)
            value *= 0.55 + 0.6 * random()

            // The edge cross through DC
            value += 0.32 * gaussian(u * u, 0.012) * (1 - radius * 0.55)
            value += 0.32 * gaussian(v * v, 0.012) * (1 - radius * 0.55)

            // Generator lattice: faint grid lines plus the peaks themselves
            value += 0.05 * (gaussian((Math.abs(u) - 0.5) ** 2, 0.008) + gaussian((Math.abs(v) - 0.5) ** 2, 0.008))
            for (const peak of SPECTRUM_PEAKS) {
                const distanceSquared = (u - peak.u) ** 2 + (v - peak.v) ** 2
                value += peak.strength * (0.9 * gaussian(distanceSquared, 0.018) + 0.22 * gaussian(distanceSquared, 0.06))
            }

            const [r, g, b] = rampColor(Math.min(1, Math.pow(Math.max(0, value), 0.85)))
            const offset = (y * size + x) * 4
            image.data[offset] = r
            image.data[offset + 1] = g
            image.data[offset + 2] = b
            image.data[offset + 3] = 255
        }
    }

    context.putImageData(image, 0, 0)
    return canvas
}
